<template>
  <div>
    <div v-for="item in items" :key="item.id">
      <div class="my-2 text-center">
        <h3>{{ $t(item.name) }}</h3>
      </div>

      <div
        v-for="asset in item.children"
        :key="asset.id"
        class="mt-1 d-flex align-center pod-asset-list-item"
        style=""
      >
        <!-- Asset name -->
        <div class="flex-grow-1">
          <div class="d-flex">
            <div class="d-flex align-center mr-2">
              <v-tooltip
                v-if="isAudioDescriptionAsset(asset)"
                location="bottom"
                z-index="1200"
                :aria-label="$t('menus.uploadFiles.assetTypeAudioDescription')"
              >
                <template v-slot:activator="{ props }">
                  <span
                    v-bind="props"
                    role="img"
                    :aria-label="$t('menus.uploadFiles.assetTypeAudioDescription')"
                    class="audio-description-icon"
                  ></span>
                </template>
                <span>{{ $t('menus.uploadFiles.assetTypeAudioDescription') }}</span>
              </v-tooltip>

              <v-tooltip
                v-if="isSystemGeneratedFile(asset)"
                location="bottom"
                z-index="1200"
                :aria-label="$t('routes.pod.thisIsSystemFile')"
              >
                <template v-slot:activator="{ props }">
                  <span
                    v-bind="props"
                    role="img"
                    :aria-label="$t('routes.pod.thisIsSystemFile')"
                    class="system-generated-file-icon"
                  >
                    <img
                      v-if="getDarkMode"
                      src="../../../../assets/images/smalltw-dark.png"
                      style="width: 20px; height: 20px;"
                      alt=""
                    />
                    <img
                      v-if="!getDarkMode"
                      src="../../../../assets/images/smalltw-light.png"
                      style="width: 20px; height: 20px;"
                      alt=""
                    />
                  </span>
                </template>
                <span>{{ $t('routes.pod.thisIsSystemFile') }}</span>
              </v-tooltip>
            </div>
            <div class="flex-grow-1">
              <span style="word-wrap: anywhere; white-space: pre-line; font-weight: 600;">
                {{ asset.name }}
              </span>

              <!-- Status or clarification -->
              <div v-if="asset.clarification != null" data-cy="podFiles-tree-clarification">
                <v-tooltip location="bottom" z-index="1200">
                  <template v-slot:activator="{ props }">
                    <small v-bind="props">{{ assetState(asset) }}</small>
                  </template>
                  <span> {{ asset.clarification }} </span>
                </v-tooltip>
              </div>
              <div v-else data-cy="podFiles-tree-state">
                <small>{{ assetState(asset) }}</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Size -->
        <div class="text-center">
          <small>{{ formatSize((asset && asset.size) ? asset.size : 0, 2) }}</small>
          <br/>
          <!-- Mimetype -->
          <small>{{ asset.mimetype }}</small>
        </div>


        <!-- Three-dot menu -->
        <div class="ml-2">
          <v-tooltip
            v-if="downloadsAllowed"
            location="bottom"
            z-index="1200"
            :aria-label="`${$t('routes.pod.downloadAsset')}: ${asset.name}`"
          >
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                @click="downloadAsset(asset)"
                :aria-label="`${$t('routes.pod.downloadAsset')}: ${asset.name}`"
                width="28"
                height="28"
                rounded="circle"
                variant="text"
                icon="mdi-download-circle-outline"
                data-cy="podFiles-tree-actions-btn"
              />
            </template>
            <span>{{ $t('routes.pod.downloadAsset') }}</span>
          </v-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useMainStore } from '@/stores/MainStore.js'
import { useCommonStore } from '@/stores/CommonStore.js'
import { useAccesslinkStore } from '@/stores/AccesslinkStore.js'
import AccesslinkCalls from '@/api/modules/AccesslinkCalls.js'
import MimetypeHelper from "../../../../components/MimetypeHelper.js"
import { formatSize } from '@/components/formatters.js';


export default {
  name: "AccesslinkAssets",
  props: {
    downloadsAllowed: {
      default: 0,
      required: true,
    },
    showOnlySubtitles: {
      type: Boolean,
      default: false,
      required: false
    },
    pod: {
      type: Object,
      required: true,
    }
  },
  methods: {
    formatSize,
    filterByType: function(availableTypes, mimetypeFilter) {
      const JOINTTYPES = [
        this.assetTypes.WEB, this.assetTypes.EXTERNAL, this.assetTypes.OTHER
      ];

      return (x) => {
        if (mimetypeFilter && JOINTTYPES.indexOf(x.type) >= 0 && mimetypeFilter(x)) {
          return true;
        }
        return availableTypes.indexOf(x.type) >= 0;
      };
    },
    filterWithExclusion: function(availableTypes) {
      const DISJOINTTYPES = [
        this.assetTypes.WEB, this.assetTypes.EXTERNAL, this.assetTypes.OTHER
      ];

      return (x) => {
        if (
          DISJOINTTYPES.indexOf(x.type) >= 0 &&
          (
            MimetypeHelper.isVideoAsset(x) ||
            MimetypeHelper.isAudioAsset(x) ||
            MimetypeHelper.isImageAsset(x)
          )
        ) {
          return false;
        }

        return availableTypes.indexOf(x.type) >= 0;
      }
    },
    async downloadAsset(asset) {
      const accesslinkStore = useAccesslinkStore();
      const accessToken = accesslinkStore.getAccesslinkInfo?.accesstoken;

      if (!asset?.id || !accessToken) {
        return;
      }

      const { url } = await AccesslinkCalls.getAccesslinkAssetDownloadUrl({
        assetid: asset.id,
        accesstoken: accessToken,
      });

      if (url) {
        window.location = url;
      }
    },
    isAudioDescriptionAsset(asset) {
      return asset.type === this.assetTypes.AUDIODESCRIPTION;
    }
  },
  computed: {
    getDarkMode() {
      const mainStore = useMainStore();
      return mainStore.getDarkMode;
    },
    assetTypes: function() {
      const commonStore = useCommonStore();
      return commonStore.assetTypes;
    },
    assetStates: function() {
      const commonStore = useCommonStore();
      return commonStore.assetStates;
    },

    stackedAssets: function() {
      let result = [];

      if (!this.pod || !this.pod.asset) {
        return result;
      }

      let keys = Object.getOwnPropertyNames(this.pod.asset);

      for(let i = 0; i < keys.length; i++) {
        result = result.concat(this.pod.asset[keys[i]]);
      }

      return result;
    },
    items: function() {
      let mainAssets = [this.assetTypes.RAW];
      let videoAssets = [this.assetTypes.VIDEO];
      let audioAssets = [this.assetTypes.AUDIO, this.assetTypes.AUDIODESCRIPTION];
      let imageAssets = [this.assetTypes.THUMBNAIL, this.assetTypes.LIVEPREVIEW, this.assetTypes.FRAME, this.assetTypes.IMAGE];
      let subsAssets = [this.assetTypes.VTT, this.assetTypes.SRT];
      let otherAssets = [this.assetTypes.EXTERNAL, this.assetTypes.SPEECHTOTEXT, this.assetTypes.OTHER];

      let treeArray;

      if (this.showOnlySubtitles) {
        treeArray = [
          { id: "str1", name: "routes.pod.assetSubtitle", children: this.stackedAssets.filter(this.filterByType(subsAssets)) },
        ]
      } else {
        treeArray = [
          { id: "str1", name: "routes.pod.assetRaw", children: this.stackedAssets.filter(this.filterByType(mainAssets)) },
          { id: "str2", name: "routes.pod.assetVideo", children: this.stackedAssets.filter(this.filterByType(videoAssets, MimetypeHelper.isVideoAsset)) },
          { id: "str3", name: "routes.pod.assetAudio", children: this.stackedAssets.filter(this.filterByType(audioAssets, MimetypeHelper.isAudioAsset)) },
          { id: "str4", name: "routes.pod.assetImage", children: this.stackedAssets.filter(this.filterByType(imageAssets, MimetypeHelper.isImageAsset)) },
          { id: "str5", name: "routes.pod.assetSubtitle", children: this.stackedAssets.filter(this.filterByType(subsAssets)) },
          { id: "str6", name: "routes.pod.assetOther", children: this.stackedAssets.filter(this.filterWithExclusion(otherAssets)) },
        ];
      }

      // truncate empty tree items
      return treeArray.filter(x => x.children.length > 0);
    },
    assetState: function() {
      const commonStore = useCommonStore();
      const states = commonStore.podStates;
      let self = this;
      return (item) => {
        if (item.broken) {
          return self.$t('routes.pod.brokenAsset');
        }
        switch (item.state) {
          case states.ERROR: return self.$t('routes.pod.stateError');
          case states.PENDING: return self.$t('routes.pod.statePending');
          case states.PROCESSING: return self.$t('routes.pod.stateProcessing');
          case states.COMPLETE: return self.$t('routes.pod.stateComplete');
        }
      }
    },
    isSystemGeneratedFile: function() {
      const systemTypes = [
        this.assetTypes.THUMBNAIL, this.assetTypes.LIVEPREVIEW,
        this.assetTypes.FRAME, this.assetTypes.RAW,
        this.assetTypes.AUDIO, this.assetTypes.WEB,
        this.assetTypes.IMAGE, this.assetTypes.VIDEO,
        // this.assetTypes.VTT, this.assetTypes.SRT,
        this.assetTypes.SPEECHTOTEXT, this.assetTypes.OTHER
      ];
      return (item) => {
        return systemTypes.indexOf(item.type) >= 0;
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.audio-description-icon {
  display: block;
  width: 20px;
  height: 20px;
  background-color: var(--tw-icons-colors-default);
  -webkit-mask: url('@/assets/images/audio-description.svg') no-repeat center / contain;
  mask: url('@/assets/images/audio-description.svg') no-repeat center / contain;
}
</style>

<style lang="scss" scoped>
.pod-asset-list-item {
  border-radius: 10px;
  padding: 10px;
  background-color: rgb(var(--v-theme-tw-surface-accent));
}

.system-generated-file-icon {
  display: inline-flex;
}

/* .v-theme--dark .pod-asset-list-item {
  background-color: #2f3640;
}

.v-theme--light .pod-asset-list-item {
  background-color: #e6ecf1;
} */
</style>
